import { batch } from 'react-redux';

import { handleErrors } from './ErrorActions';
import { addSuccess } from './SuccessActions';
import * as upgradeModulesApi from '../api/upgrade-modules';
import { convertLogsToArray } from '../containers/UpgradeModules/utils';

export const SET_UPGRADE_MODULES = 'SET_UPGRADE_MODULES';
export const CHANGE_IS_FETCHING_MODULE = 'CHANGE_IS_FETCHING_MODULE';
export const SET_CURRENT_UPGRADE_MODULE_ID = 'SET_CURRENT_UPGRADE_MODULE_ID';
export const UPDATE_MODULE_STATUS_AND_LOGS = 'UPDATE_MODULE_STATUS_AND_LOGS';
export const SET_MODULE_STATUS = 'SET_MODULE_STATUS';
export const CLEAR_UPGRADE_MODULES_STORE = 'CLEAR_UPGRADE_MODULES_STORE';
export const SET_MODULE_WITH_INITIAL_INPROGRESS_STATUS =
  'SET_MODULE_WITH_INITIAL_INPROGRESS_STATUS';

const POLLING_TIME = process.env.NODE_ENV === 'test' ? 0 : 15000;

export const STATUSES = {
  COMPLETED: 'Completed',
  IN_PROGRESS: 'InProgress',
  ERROR: 'Error',
};

const setUpgradeModules = (modules, formConversionLogFile) => ({
  type: SET_UPGRADE_MODULES,
  payload: {
    modules,
    formConversionLogFile,
  },
});

const updateModuleStatusAndLogs = (module, formConversionLogFile) => ({
  type: UPDATE_MODULE_STATUS_AND_LOGS,
  payload: {
    module,
    formConversionLogFile,
  },
});

const changeIsFetchingModule = (flag) => ({
  type: CHANGE_IS_FETCHING_MODULE,
  payload: {
    flag,
  },
});

const setCurrentUpgradeModuleId = (moduleId) => ({
  type: SET_CURRENT_UPGRADE_MODULE_ID,
  payload: {
    moduleId,
  },
});

const setModuleStatus = (moduleId, status) => ({
  type: SET_MODULE_STATUS,
  payload: {
    moduleId,
    status,
  },
});

export const setModuleWithInitialInProgressStatus = (module) => ({
  type: SET_MODULE_WITH_INITIAL_INPROGRESS_STATUS,
  payload: {
    module,
  },
});

export const clearUpgradeModulesStore = () => ({
  type: CLEAR_UPGRADE_MODULES_STORE,
});

export const checkStatus = (module, moduleId, timeoutRef, stopPoolingRef, dispatch) => {
  const { Status: status, Message: message } = module;

  switch (status) {
    case STATUSES.IN_PROGRESS: {
      if (stopPoolingRef.current) {
        return;
      }
      timeoutRef.current = setTimeout(async () => {
        upgradeModuleStatus(moduleId, timeoutRef, stopPoolingRef, dispatch);
      }, POLLING_TIME);
      return;
    }

    case STATUSES.COMPLETED: {
      dispatch(addSuccess({ message: message || 'Module upgraded successfully' }));
      changeFetchingData(false, null, dispatch);
      return;
    }

    case STATUSES.ERROR: {
      dispatch(handleErrors({ message: 'Error has occurred. Refer to the logs' }));
      changeFetchingData(false, null, dispatch);
      return;
    }

    default: {
      dispatch(handleErrors({ message: 'Unexpected Status' }));
      changeFetchingData(false, null, dispatch);
      return;
    }
  }
};

export const changeFetchingData = (isFetchingModule, currentUpgradeModuleId, dispatch) => {
  batch(() => {
    dispatch(changeIsFetchingModule(isFetchingModule));
    dispatch(setCurrentUpgradeModuleId(currentUpgradeModuleId));
  });
};

export const upgradeModuleStatus = async (moduleId, timeoutRef, stopPoolingRef, dispatch) => {
  const upgradeModuleStatusResponse = await upgradeModulesApi.getUpgradeModuleStatus(moduleId);

  if (!upgradeModuleStatusResponse.hasError) {
    const {
      Installations: [module],
      FormConversionLogFile: formConversionLogFile,
    } = upgradeModuleStatusResponse.Status;

    dispatch(updateModuleStatusAndLogs(convertLogsToArray(module), formConversionLogFile));
    checkStatus(module, moduleId, timeoutRef, stopPoolingRef, dispatch);
  } else {
    dispatch(handleErrors(upgradeModuleStatusResponse.errors));
    dispatch(setModuleStatus(moduleId, STATUSES.ERROR));
    changeFetchingData(false, null, dispatch);
  }
};

export const initiateModuleUpgrade = () => async (dispatch) => {
  const upgradeModulesResponse = await upgradeModulesApi.initiateModuleUpgrade();

  if (!upgradeModulesResponse.hasError) {
    const {
      Installations: modules,
      FormConversionLogFile: formConversionLogFile,
    } = upgradeModulesResponse.Status;

    dispatch(setUpgradeModules(convertLogsToArray(modules), formConversionLogFile));

    const inprogressModule = upgradeModulesResponse.Status.Installations.find(
      (module) => module.Status === STATUSES.IN_PROGRESS
    );
    dispatch(setModuleWithInitialInProgressStatus(inprogressModule ?? null));
  } else {
    dispatch(handleErrors(upgradeModulesResponse.errors));
  }
};

export const getUpgradeModules = ({ moduleId, timeoutRef, stopPoolingRef }) => async (dispatch) => {
  dispatch(setModuleStatus(moduleId, STATUSES.IN_PROGRESS));
  changeFetchingData(true, moduleId, dispatch);
  const checkModuleForUpgradeResponse = await upgradeModulesApi.getUpgradeModules(moduleId);

  if (!checkModuleForUpgradeResponse.hasError) {
    if (checkModuleForUpgradeResponse.UpgradeModule) {
      upgradeModuleStatus(moduleId, timeoutRef, stopPoolingRef, dispatch);
    } else {
      dispatch(handleErrors({ message: 'Cannot upgrade Module' }));
      changeFetchingData(false, null, dispatch);
    }
  } else {
    dispatch(handleErrors(checkModuleForUpgradeResponse.errors));
    changeFetchingData(false, null, dispatch);
  }
};

export const resetModuleUpgrade = (currentUpgradeModuleId, stopPoolingRef, timeoutRef) => async (
  dispatch
) => {
  const resetUpgradeStatusResponse = await upgradeModulesApi.resetUpgradeStatus(
    currentUpgradeModuleId
  );

  if (!resetUpgradeStatusResponse.hasError) {
    const {
      Installations: [module],
      FormConversionLogFile: formConversionLogFile,
    } = resetUpgradeStatusResponse.Status;

    stopPoolingRef.current = true;
    clearInterval(timeoutRef.current);
    changeFetchingData(false, null, dispatch);

    dispatch(updateModuleStatusAndLogs(convertLogsToArray(module), formConversionLogFile));
  } else {
    dispatch(handleErrors(resetUpgradeStatusResponse.errors));
  }
};
